import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar as CalendarIcon, X, InfoIcon, Clock } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { IconPicker } from './IconPicker';
import { BackgroundPicker } from './BackgroundPicker';
import { VariablePillEditor } from './VariablePillEditor';

export function WorkflowSetupModal({ open, onOpenChange, onSave, initialData = null, projectId, workflowId, isCompanyProcess = false, userProfile, webhookVariables }) {
  // Use "Process" terminology when in company process mode
  const defaultName = isCompanyProcess ? 'My Process' : 'My Workflow';
  const entityLabel = isCompanyProcess ? 'process' : 'workflow';
  const entityLabelCapitalized = isCompanyProcess ? 'Process' : 'Workflow';

  // Build real user data for preview instead of hardcoded values
  const userName = userProfile ? `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() : undefined;
  const previewOverrides = userName ? {
    'current_user.name': userName,
    'current_user.email': userProfile.email || '',
    'current_user.first_name': userProfile.first_name || '',
  } : undefined;

  const [workflowName, setWorkflowName] = useState(initialData?.workflowName || defaultName);
  const [runName, setRunName] = useState(initialData?.runName || '{{ current_user.name }} - {{ current_date.full }}');
  const [dueDate, setDueDate] = useState(initialData?.dueDate || null);
  const [description, setDescription] = useState(initialData?.description || '');
  const [runComments, setRunComments] = useState(initialData?.runComments || false);
  const [selectedBackground, setSelectedBackground] = useState(initialData?.background || {
    type: 'gradient',
    value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    id: 'blue',
    name: 'Blue Wave',
  });
  const [selectedNotificationCover, setSelectedNotificationCover] = useState(initialData?.notificationCover || {
    type: 'gradient',
    value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    id: 'blue',
    name: 'Blue Wave',
  });
  const [selectedIcon, setSelectedIcon] = useState(initialData?.icon || null);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [showBackgroundPicker, setShowBackgroundPicker] = useState(false);
  const [showNotificationCoverPicker, setShowNotificationCoverPicker] = useState(false);

  // Update state when initialData changes (e.g., when reopening modal with different data)
  React.useEffect(() => {
    if (initialData) {
      setWorkflowName(initialData.workflowName || defaultName);
      setRunName(initialData.runName || '{{ current_user.name }} - {{ current_date.full }}');
      setDueDate(initialData.dueDate || null);
      setDescription(initialData.description || '');
      setRunComments(initialData.runComments || false);
      setSelectedBackground(initialData.background || {
        type: 'gradient',
        value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        id: 'blue',
        name: 'Blue Wave',
      });
      setSelectedNotificationCover(initialData.notificationCover || {
        type: 'gradient',
        value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        id: 'blue',
        name: 'Blue Wave',
      });
      setSelectedIcon(initialData.icon || null);
    }
  }, [initialData]);

  const handleSave = () => {
    onSave({
      workflowName,
      runName,
      dueDate,
      description,
      runComments,
      background: selectedBackground,
      notificationCover: selectedNotificationCover,
      icon: selectedIcon,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start gap-2">
            <div className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold flex-shrink-0 mt-0.5">
              1
            </div>
            <div className="flex-1">
              <DialogTitle className="text-lg">Customise your {entityLabel}</DialogTitle>
              <DialogDescription className="text-sm mt-1">
                {isCompanyProcess
                  ? 'A process is a reusable workflow template that can be shared across spaces in your company.'
                  : 'A workflow is a set of instructions that lay out exactly how a process should be completed.'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Workflow/Process Name */}
          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <Label htmlFor="workflow-name">{entityLabelCapitalized} Name</Label>
              <InfoIcon className="h-3 w-3 text-muted-foreground" />
            </div>
            <Input
              id="workflow-name"
              value={workflowName}
              onChange={(e) => setWorkflowName(e.target.value)}
              placeholder={defaultName}
            />
          </div>

          {/* Process/Workflow Run Name */}
          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <Label>{entityLabelCapitalized} Run Name</Label>
              <InfoIcon className="h-3 w-3 text-muted-foreground" />
            </div>
            <VariablePillEditor
              value={runName}
              onChange={setRunName}
              placeholder="Add text and variables for run name..."
              exampleData={previewOverrides}
              webhookVariables={webhookVariables}
            />
          </div>

          {/* Process/Workflow Run Default Due Date - Relative Duration */}
          <div className="space-y-2">
            <Label>Default Due Date</Label>
            <p className="text-xs text-muted-foreground">Runs will be due this long after they are started.</p>
            {dueDate ? (
              <div className="space-y-3 p-3 rounded-lg border bg-muted/30">
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <Input
                      type="number"
                      min="0"
                      value={dueDate.months || 0}
                      onChange={(e) => setDueDate({ ...dueDate, months: Math.max(0, parseInt(e.target.value) || 0) })}
                      className="text-center h-9 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <div className="text-[10px] text-center mt-1 text-muted-foreground">months</div>
                  </div>
                  <div>
                    <Input
                      type="number"
                      min="0"
                      value={dueDate.days || 0}
                      onChange={(e) => setDueDate({ ...dueDate, days: Math.max(0, parseInt(e.target.value) || 0) })}
                      className="text-center h-9 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <div className="text-[10px] text-center mt-1 text-muted-foreground">days</div>
                  </div>
                  <div>
                    <Input
                      type="number"
                      min="0"
                      value={dueDate.hours || 0}
                      onChange={(e) => setDueDate({ ...dueDate, hours: Math.max(0, parseInt(e.target.value) || 0) })}
                      className="text-center h-9 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <div className="text-[10px] text-center mt-1 text-muted-foreground">hours</div>
                  </div>
                  <div>
                    <Input
                      type="number"
                      min="0"
                      value={dueDate.minutes || 0}
                      onChange={(e) => setDueDate({ ...dueDate, minutes: Math.max(0, parseInt(e.target.value) || 0) })}
                      className="text-center h-9 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <div className="text-[10px] text-center mt-1 text-muted-foreground">minutes</div>
                  </div>
                </div>
                <Select
                  value={dueDate.businessDayHandling || 'all_days'}
                  onValueChange={(val) => setDueDate({ ...dueDate, businessDayHandling: val })}
                >
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_days">All days (including weekends)</SelectItem>
                    <SelectItem value="shift_next">Skip weekends (shift to Monday)</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-xs text-destructive hover:text-destructive h-7 px-2"
                  onClick={() => setDueDate(null)}
                >
                  <X className="h-3 w-3 mr-1" />
                  Remove due date
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setDueDate({ days: 7, months: 0, hours: 0, minutes: 0, businessDayHandling: 'all_days' })}
              >
                <Clock className="h-4 w-4 mr-2" />
                Set default due date
              </Button>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={`What will this ${entityLabel} be used for?`}
              rows={4}
            />
          </div>

          {/* Process/Workflow Run Comments */}
          <div className="flex items-center justify-between">
            <Label htmlFor="run-comments" className="cursor-pointer">
              {entityLabelCapitalized} Run Comments
            </Label>
            <Switch
              id="run-comments"
              checked={runComments}
              onCheckedChange={setRunComments}
            />
          </div>

          {/* Design Section */}
          <div className="space-y-3 pt-2">
            <Label>Design</Label>

            {/* Icon */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm">Icon</span>
                {selectedIcon && (() => {
                  const IconComponent = LucideIcons[selectedIcon];
                  return IconComponent ? (
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-md bg-muted">
                        <IconComponent className="h-5 w-5" />
                      </div>
                      <span className="text-xs text-muted-foreground">{selectedIcon}</span>
                    </div>
                  ) : null;
                })()}
              </div>
              <div className="flex items-center gap-2">
                {selectedIcon && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedIcon(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  variant="link"
                  size="sm"
                  className="text-primary"
                  onClick={() => setShowIconPicker(true)}
                >
                  {selectedIcon ? 'Change' : 'Add'}
                </Button>
              </div>
            </div>

            {/* Background */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm">Background</span>
                <div className="flex items-center gap-2">
                  {selectedBackground && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedBackground(null)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="link"
                    size="sm"
                    className="text-primary"
                    onClick={() => setShowBackgroundPicker(true)}
                  >
                    {selectedBackground ? 'Change' : 'Add'}
                  </Button>
                </div>
              </div>
              <button
                className="w-full h-32 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity bg-cover bg-center"
                onClick={() => setShowBackgroundPicker(true)}
                style={
                  selectedBackground
                    ? selectedBackground.type === 'image'
                      ? {
                          backgroundImage: `url(${selectedBackground.value})`,
                        }
                      : {
                          background: selectedBackground.value,
                        }
                    : { background: '#e5e7eb' }
                }
              >
                {selectedBackground ? (
                  <div className="h-full flex items-center justify-center bg-black/0 hover:bg-black/10 transition-colors">
                    <span className="text-white text-sm bg-black/50 px-3 py-1 rounded-full backdrop-blur-sm">
                      {selectedBackground.name}
                    </span>
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    Click to add background
                  </div>
                )}
              </button>
            </div>

            {/* Notification Cover */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm">Notification cover</span>
                <div className="flex items-center gap-2">
                  {selectedNotificationCover && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedNotificationCover(null)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="link"
                    size="sm"
                    className="text-primary"
                    onClick={() => setShowNotificationCoverPicker(true)}
                  >
                    {selectedNotificationCover ? 'Change' : 'Add'}
                  </Button>
                </div>
              </div>
              {selectedNotificationCover && (
                <button
                  className="w-full h-32 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity bg-cover bg-center"
                  onClick={() => setShowNotificationCoverPicker(true)}
                  style={
                    selectedNotificationCover.type === 'image'
                      ? {
                          backgroundImage: `url(${selectedNotificationCover.value})`,
                        }
                      : {
                          background: selectedNotificationCover.value,
                        }
                  }
                >
                  <div className="h-full flex items-center justify-center bg-black/0 hover:bg-black/10 transition-colors">
                    <span className="text-white text-sm bg-black/50 px-3 py-1 rounded-full backdrop-blur-sm">
                      {selectedNotificationCover.name}
                    </span>
                  </div>
                </button>
              )}
            </div>
          </div>

        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Bulk Create Tasks
          </Button>
          <Button onClick={handleSave}>
            Save
          </Button>
        </div>
      </DialogContent>

      {/* Icon Picker Modal */}
      <IconPicker
        open={showIconPicker}
        onOpenChange={setShowIconPicker}
        onSelectIcon={setSelectedIcon}
        currentIcon={selectedIcon}
      />

      {/* Background Picker Modal */}
      <BackgroundPicker
        open={showBackgroundPicker}
        onOpenChange={setShowBackgroundPicker}
        onSelectBackground={setSelectedBackground}
        currentBackground={selectedBackground}
        projectId={projectId}
        taskId={workflowId || 'setup'}
      />

      {/* Notification Cover Picker Modal */}
      <BackgroundPicker
        open={showNotificationCoverPicker}
        onOpenChange={setShowNotificationCoverPicker}
        onSelectBackground={setSelectedNotificationCover}
        currentBackground={selectedNotificationCover}
        projectId={projectId}
        taskId={workflowId || 'setup'}
      />
    </Dialog>
  );
}
